import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getActivePackagesByRoom, getCalendarsToBeFetchedByRoom, getMultiRoomPackageTotals, isLoadingByRoom, LoadingStatusByRoom, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import MultiRoomOfferSelectRoomsButton from '../MultiRoomOfferSelectRoomsButton'
import MultiRoomOfferAddToCartButton from '../MultiRoomOfferAddToCartButton'
import MultiRoomOfferBookButton from '../MultiRoomOfferBookButton'
import MobileMultipleRoomsDurationLabel from './MobileMultipleRoomsDurationLabel'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { EmptyArray } from 'lib/array/arrayUtils'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import TextButton from 'components/Luxkit/Button/TextButton'
import Sheet from 'components/utils/Sheet'
import BodyText from 'components/Luxkit/Typography/BodyText'

const FooterContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  rooms: Array<App.RoomOccupants>;
  checkIn?: string;
  isFlightsSelected: boolean;
  multiItemCartEnabled: boolean;
  multiRoomPackages: PackageOptionViewByRoom;
  multiRoomPackageTotals: MultiRoomPackageTotals;
  isLoadingByRoom: LoadingStatusByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  canRedeemLuxPlusBenefits: boolean;
}

function MobileMultipleRoomsIndividualRoomsFooter(props: Props) {
  const { offer, rooms, checkIn, isFlightsSelected, multiItemCartEnabled, multiRoomPackages, multiRoomPackageTotals, isLoadingByRoom, calendarsToBeFetchedByRoom, canRedeemLuxPlusBenefits } = props

  const {
    hotelPrice,
    hotelMemberPrice,
    hotelPlusFlightsPrice,
    hotelPlusFlightsMemberPrice,
    flightPrice,
  } = multiRoomPackageTotals

  const showModal = useContext(ModalContext)
  const currentContact = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)

  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && hotelMemberPrice > 0

  const isBookNowDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(multiRoomPackages).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, multiRoomPackages])

  const loyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView })
    ))
  }, [offer, multiRoomPackages])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...loyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice,
      skip: !isFlightsSelected,
    }),
  ], [offer, isFlightsSelected, flightPrice, loyaltyHotelOptions])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS"/>)
  }, [showModal])

  return (
    <FooterContainer>
      <Group direction="vertical" gap={20}>
        {hotelPrice > 0 && <>
          <div>
            <PriceRowPriceCaption>
              <MobileMultipleRoomsDurationLabel shouldDisplayRoom/>
            </PriceRowPriceCaption>
            <LuxPlusPriceStack
              size="S"
              price={isFlightsSelected ? hotelPlusFlightsPrice : hotelPrice}
              memberPrice={isFlightsSelected ? hotelPlusFlightsMemberPrice : hotelMemberPrice}
              saleUnit={isFlightsSelected && !hasFlights ? 'total*' : 'total'}
              testid="pricing"
              offerConfig={offer.luxPlus}
              leadWithLuxPlusPrice
            />
          </div>
          <LoyaltyPoints
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
          />
        </>}
        {!shouldPreventMultipleRoomsBooking && <>
          {isFlightsSelected && <MultiRoomOfferSelectRoomsButton size="large" fit="flex" disabled={isBookNowDisabled} />}
          {!isFlightsSelected && <Group direction="horizontal" gap={12} horizontalAlign="stretch">
            {multiItemCartEnabled && checkIn && <MultiRoomOfferAddToCartButton size="large" fit="flex" disabled={isBookNowDisabled} />}
            <MultiRoomOfferBookButton size="large" fit="flex" disabled={isBookNowDisabled} />
          </Group>}
        </>}
        {shouldPreventMultipleRoomsBooking && (
          <Sheet background="neutral-seven" padding={20}>
            <Group direction="vertical" gap={8}>
              <Group direction="vertical" gap={4}>
                <BodyText variant="medium" weight="bold">Ready to book? Call us to complete your booking</BodyText>
                <BodyText variant="medium">
                  Our team are available {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}
                </BodyText>
              </Group>
              <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${currentContact.defaultContact.number}`} fit="flex">
                Call {currentContact.defaultContact.humanReadable}
              </TextButton>
              {callbacksFeatureEnabled && (
                <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
                  Request a callback
                </TextButton>
              )}
            </Group>
          </Sheet>
        )}
      </Group>
    </FooterContainer>
  )
}

type MappedProps = Pick<Props,
  'offer'
  | 'rooms'
  | 'checkIn'
  | 'isFlightsSelected'
  | 'multiItemCartEnabled'
  | 'multiRoomPackages'
  | 'multiRoomPackageTotals'
  | 'isLoadingByRoom'
  | 'calendarsToBeFetchedByRoom'
  | 'canRedeemLuxPlusBenefits'
>
export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  multiRoomPackages: getActivePackagesByRoom(state, appState),
  multiRoomPackageTotals: getMultiRoomPackageTotals(state, appState),
  multiItemCartEnabled: isMultiCartEnabled(appState),
  isLoadingByRoom: isLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
}), MobileMultipleRoomsIndividualRoomsFooter)
